import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import TouchAppRoundedIcon from '@mui/icons-material/TouchAppRounded';
import GuestService from '../../services/GuestService';
import { chatColors } from '../chat/chatTheme';

function PeopleSection({ title, action, children, empty }) {
    return (
        <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {title}
                </Typography>
                {action}
            </Box>
            {empty ? (
                <Typography variant="body2" color="text.disabled">
                    None
                </Typography>
            ) : (
                children
            )}
        </Box>
    );
}

// lastSeen arrives as an ISO instant (UTC, "Z" suffix) from the GraphQL
// LocalDateTime scalar, so Date parses it into the viewer's local time.
function formatLastSeen(lastSeen) {
    const seen = new Date(lastSeen);
    if (!lastSeen || isNaN(seen)) return 'Last seen: unknown';
    const minutes = Math.floor((Date.now() - seen) / 60000);
    if (minutes < 1) return 'Last seen moments ago';
    if (minutes < 60) return `Last seen ${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `Last seen ${hours} hour${hours === 1 ? '' : 's'} ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `Last seen ${days} day${days === 1 ? '' : 's'} ago`;
    return `Last seen ${seen.toLocaleDateString()}`;
}

export default function ({ roomData, isAdmin = false, onToggleUser, onToggleAll }) {
    const [people, setPeople] = useState([]);
    const [guestInfo, setGuestInfo] = useState({ count: 0, guests: [] });

    useEffect(() => {
        // One consolidated list: online admins pinned first, then everyone else
        // online, then (admin view only) offline members who have previously
        // been in the room. Roommates arrive from the backend as an unordered
        // Set, so sort by name (tie-break on userId) within each group to keep
        // the list stable across refreshes/toggles.
        // Guest view gets a trimmed RoomDto with no userId per roommate (id is
        // member-view-only), so tie-break/key logic below must tolerate its absence.
        const groupRank = (p) => (!p.online ? 2 : p.admin ? 0 : 1);
        const byName = (a, b) =>
            a.name.localeCompare(b.name) || (a.userId && b.userId ? a.userId.localeCompare(b.userId) : 0);
        const roomPeople = (roomData.roommates || [])
            .map((r, index) => ({
                name: `${r.user.firstName} ${r.user.lastName}`,
                userId: r.id?.userId,
                key: r.id?.userId ?? `${r.user.firstName}-${r.user.lastName}-${index}`,
                admin: r.role === 'ADMIN',
                online: !!r.online,
                lastSeen: r.lastSeen,
                // Per-user interactivity defaults on; Presentation Mode
                // bulk-resets it (off when it turns on, back on when off).
                interactive: r.interactive !== false
            }))
            .filter((p) => p.online || isAdmin)
            .sort((a, b) => groupRank(a) - groupRank(b) || byName(a, b));
        setPeople(roomPeople);
    }, [roomData, isAdmin]);

    // Poll for guest presence
    useEffect(() => {
        if (!roomData?.url) return;

        function fetchGuests() {
            GuestService.getGuestInfo(roomData.url)
                .then(setGuestInfo)
                .catch(() => {});
        }

        fetchGuests();
        const interval = setInterval(fetchGuests, 20000);
        return () => clearInterval(interval);
    }, [roomData?.url]);

    const onlineOthers = people.filter((p) => p.online && !p.admin);
    // Header toggle stays on while anyone is interactive; it's off only when
    // everyone is off. So one tap can turn everyone off, and once all are off
    // the same tap turns everyone back on.
    const anyInteractive = onlineOthers.some((p) => p.interactive);

    return (
        <Box>
            <PeopleSection
                title="Members"
                empty={people.length === 0}
                action={
                    isAdmin && onlineOthers.length > 0 && (
                        <Tooltip title="Set interactivity for everyone">
                            <IconButton
                                size="small"
                                onClick={() => onToggleAll && onToggleAll(!anyInteractive)}
                                sx={{ color: anyInteractive ? 'primary.main' : 'action.disabled' }}
                            >
                                <TouchAppRoundedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )
                }
            >
                {people.map((p) => (
                    <Box
                        key={p.key}
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 30 }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                            {p.online ? (
                                <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                                    {p.name}
                                </Typography>
                            ) : (
                                <Tooltip title={formatLastSeen(p.lastSeen)}>
                                    <Typography variant="body2" noWrap color="text.disabled" sx={{ minWidth: 0 }}>
                                        {p.name}
                                    </Typography>
                                </Tooltip>
                            )}
                            {p.admin && (
                                <Chip
                                    label="Admin"
                                    size="small"
                                    // Filled like the chat's admin avatars - white on the same orange -
                                    // so the colour means admin everywhere.
                                    sx={(theme) => ({
                                        height: 18,
                                        fontSize: '0.65rem',
                                        color: theme.palette.common.white,
                                        backgroundColor: chatColors(theme).adminAvatar,
                                        opacity: p.online ? 1 : 0.5
                                    })}
                                />
                            )}
                        </Box>
                        {isAdmin && p.online && !p.admin && (
                            <Tooltip
                                title={
                                    p.interactive
                                        ? `Turn off interaction for ${p.name} (Presentation Mode settings apply)`
                                        : `Turn on interaction for ${p.name} (full access)`
                                }
                            >
                                <IconButton
                                    size="small"
                                    onClick={() => onToggleUser && onToggleUser(p.userId, !p.interactive)}
                                    sx={{ color: p.interactive ? 'primary.main' : 'action.disabled' }}
                                >
                                    <TouchAppRoundedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                ))}
            </PeopleSection>

            {guestInfo.count > 0 && (
                <PeopleSection title={`Guests (${guestInfo.count})`}>
                    {guestInfo.guests.map((g, i) => (
                        <div key={`guest-${i}`}>{g.displayName || 'Guest'}</div>
                    ))}
                </PeopleSection>
            )}
        </Box>
    );
}
